import { type IReactionDisposer, makeAutoObservable, reaction } from 'mobx'
import { nanoid } from 'nanoid'
import { prepareSearchPhoneNumber } from 'shared/lib'
import { TableStore } from 'shared/ui/TableNew/store/tableStore'
import modalFullScreenStore from 'shared/ui/ModalFullScreen/store/modalFullScreenStore'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { RowInbox } from 'entities/Inbox/model/Table/RowInbox'
import { type Inbox } from 'entities/Inbox/model/Inbox'
import { type Phone } from 'entities/Phone/model/Phone'
import { type IUser } from 'entities/Users'
import { AssignMemberToInbox } from 'pages/settings/pages/members/ui/AssignMemberToInbox'
import { AssignMemberToInboxStore } from 'pages/settings/pages/members/ui/AssignMemberToInbox/store/assignMemberToInboxStore'
import { SimpleAssignFlow } from 'pages/settings/pages/members/ui/AssignMemberToInbox/store/flows'
import { type SettingsStore } from 'pages/settings/store/settingsStore'

export class MemberInboxesStore {
  private readonly _tableStore: TableStore<RowInbox>
  private _search = ''
  private _memberId = 0
  private _disposeRowsLengthReaction: IReactionDisposer | null = null
  readonly modalId = nanoid()
  private _assignMemberToInboxStore: AssignMemberToInboxStore

  constructor(private _entities: IEntitiesStore, readonly settings: SettingsStore) {
    this._tableStore = new TableStore({
      key: 'memberInboxesTable',
      element: 'inbox',
      limit: 1000,
      page: 1,
      useColumnsCache: false,
    })

    const flow = new SimpleAssignFlow(
      () => this._assignMemberToInboxStore.selectedMember?.id ?? null,
      this.addMembersToExistingInbox
    )

    this._assignMemberToInboxStore = new AssignMemberToInboxStore({
      entities: this._entities,
      settings: this.settings,
      mode: 'simple',
      onCancel: this.onCloseAssignInboxModal,
      flow,
    })

    makeAutoObservable(this)
    this.reactionRowsLength()
  }

  private addMembersToExistingInbox = async (
    memberId: number,
    inboxIds: number[]
  ): Promise<boolean> => {
    return this._assignMemberToInboxStore.assignToExistingInboxStore.addMembersToExistingInbox(
      memberId,
      inboxIds
    )
  }

  get search() {
    return this._search
  }

  get tableStore() {
    return this._tableStore
  }

  get inboxesRows() {
    if (!this._memberId) return []

    return this.allInboxes
      .filter((inbox: Inbox) => inbox.memberIds.includes(this._memberId))
      .filter((inbox: Inbox) => {
        if (this.search.trim()) {
          const searchTerm = this.search.toLowerCase().trim()
          const numberSearchTerm = prepareSearchPhoneNumber(searchTerm)

          return (
            inbox.name.toLowerCase().includes(searchTerm) ||
            inbox.numbers.some((number: Phone) =>
              prepareSearchPhoneNumber(number.number).includes(numberSearchTerm)
            )
          )
        }
        return true
      })
      .map((inbox: Inbox) => new RowInbox(inbox, this._entities))
  }

  get allInboxes() {
    return this._entities.inboxesStore.organizationInboxes
  }

  get memberInboxIds() {
    if (!this._memberId) return []

    return this.allInboxes
      .filter((inbox) => inbox.memberIds.includes(this._memberId))
      .map((inbox) => inbox.id)
  }

  get InboxesRowsLength() {
    return this.inboxesRows.length
  }

  get isInboxAssignAvailable() {
    const currentUserId = this._entities.usersStore.user?.id

    return this.allInboxes.some(
      (inbox) => inbox.ownerId === currentUserId && !this.memberInboxIds.includes(inbox.id)
    )
  }

  get isAssignInboxButtonShown() {
    const permissionCanManageOrganizationInboxes = this._entities.userPermissionsStore.getItem(
      'can_manage_organization_inboxes'
    )
    const isUserHasInboxOwnership = this._entities.usersStore.user?.origin.is_owned_teams ?? false

    return (
      permissionCanManageOrganizationInboxes ||
      (isUserHasInboxOwnership && this.isInboxAssignAvailable)
    )
  }

  setSearch = (value: string) => {
    this._search = value
  }

  setMemberId = (memberId: number) => {
    this._memberId = memberId
  }

  onCloseAssignInboxModal = () => {
    this._assignMemberToInboxStore.reset()
    modalFullScreenStore.removeModal(this.modalId)
  }

  assignInboxModal = (member: IUser) => {
    this._assignMemberToInboxStore.setMember(member)

    modalFullScreenStore.addModal({
      id: this.modalId,
      title: 'Assign inbox',
      ModalContent: AssignMemberToInbox,
      ModalContentProps: {
        store: this._assignMemberToInboxStore,
      },
      onClose: this.onCloseAssignInboxModal,
    })
  }

  reset = () => {
    this._disposeRowsLengthReaction?.()
    this._disposeRowsLengthReaction = null
    this._search = ''
    this._memberId = 0
    this.tableStore.setTotal(0)
  }

  reactionRowsLength = () => {
    this._disposeRowsLengthReaction?.()
    this._disposeRowsLengthReaction = reaction(
      () => this.InboxesRowsLength,
      () => {
        this.tableStore.setTotal(this.InboxesRowsLength)
      }
    )
  }
}
